import {DEFAULT_SESSION_VIEW} from '../session/sessionViewState'
import type {SessionViewState} from '../session/sessionViewState'
import type {BlindLevel, Schedule, ScheduledBreak} from '../types/domain'

import {colorUp} from '../timer/chips/colorUpCheck'

export const SET_SESSION_VIEW = 'blinds-schedule/session/setView'

export const UPDATE_TIMER = 'blinds-schedule/schedule/updateTimer'

export const RESET_BLIND = 'blinds-schedule/schedule/resetBlind'
export const START_NEXT_BLIND = 'blinds-schedule/schedule/startNextBlind'
export const FINISH_BREAK = 'blinds-schedule/schedule/finishBreak'
export const RESET_BREAK = 'blinds-schedule/schedule/resetBreak'
export const START_BREAK = 'blinds-schedule/schedule/startBreak'
export const CLOCK_TICK = 'blinds-schedule/schedule/clockTick'
export const SWITCH_TO_NEW_SCHEDULE = 'blinds-schedule/schedule/switchToNewSchedule'

export interface ScheduleState extends Schedule {
  hasStarted?: boolean
  sessionView?: SessionViewState
  timerPlaying?: boolean
  timerDeadlineMs?: number | null
  timerRemainingMs?: number
  timerRevision?: number
  currentBlindNumber: number
  nextBreakNumber: number
  onBreak: boolean
  secondsLeftInCurrentSession: number
}

export type ScheduleAction =
  | {type: typeof SET_SESSION_VIEW; payload: Partial<SessionViewState>}
  | {type: typeof UPDATE_TIMER; payload: Partial<ScheduleState>; publish: boolean}
  | {type: typeof SWITCH_TO_NEW_SCHEDULE; payload: Schedule}
  | {type: typeof RESET_BREAK | typeof START_BREAK; payload: ScheduledBreak}
  | {type: typeof CLOCK_TICK; payload: number}
  | {type: typeof RESET_BLIND | typeof FINISH_BREAK}
  | {type: typeof START_NEXT_BLIND; payload?: BlindLevel}

export const DEFAULT_STATE: ScheduleState = {
  name: '',
  hasStarted: false,
  blinds: [],
  levelLength: 15,
  breaks: [],
  chips: [],
  currentBlindNumber: 0,
  nextBreakNumber: 0,
  onBreak: false,
  secondsLeftInCurrentSession: 15 * 60,
}

export default function reducer(state: ScheduleState = DEFAULT_STATE, action: ScheduleAction): ScheduleState {
  switch (action.type) {
    case SET_SESSION_VIEW:
      return {...state, sessionView: {...(state.sessionView ?? DEFAULT_SESSION_VIEW), ...action.payload}}
    case UPDATE_TIMER:
      return {...state, ...action.payload, timerRevision: (state.timerRevision ?? 0) + (action.publish ? 1 : 0)}
    case SWITCH_TO_NEW_SCHEDULE:
      return {
        ...DEFAULT_STATE,
        ...action.payload,
        timerRemainingMs: action.payload.levelLength * 60000,
        secondsLeftInCurrentSession: action.payload.levelLength * 60
      }
    case RESET_BLIND:
      return {
        ...state,
        currentBlindNumber: state.currentBlindNumber,
        onBreak: false,
        timerRemainingMs: state.levelLength * 60000,
        secondsLeftInCurrentSession: state.levelLength * 60
      }
    case RESET_BREAK:
      return {...state, secondsLeftInCurrentSession: action.payload.length * 60, timerRemainingMs: action.payload.length * 60000}
    case CLOCK_TICK:
      return {...state, secondsLeftInCurrentSession: action.payload, timerRemainingMs: action.payload * 1000}
    case FINISH_BREAK:
      return {
        ...state,
        nextBreakNumber: state.nextBreakNumber + 1,
        onBreak: false,
        timerRemainingMs: state.levelLength * 60000,
        secondsLeftInCurrentSession: state.levelLength * 60
      }
    case START_BREAK:
      return {
        ...state,
        onBreak: true,
        timerRemainingMs: action.payload.length * 60000,
        secondsLeftInCurrentSession: action.payload.length * 60,
        chips: colorUp(state.chips, state.blinds, state.currentBlindNumber + 1, false),
      }
    case START_NEXT_BLIND:
      return {
        ...state,
        currentBlindNumber: state.currentBlindNumber + 1,
        onBreak: false,
        timerRemainingMs: state.levelLength * 60000,
        secondsLeftInCurrentSession: state.levelLength * 60,
        chips: colorUp(state.chips, state.blinds, state.currentBlindNumber + 1),
      }
    default:
      return state
  }
}
